"use client";

import { useForm } from "react-hook-form";
import { useState } from "react";
import WarningModal from "../shared/WarningModal";
import Input from "../ui/Input";
import Button from "../ui/Button";
import { useRouter } from "next/navigation";
import { useAuth } from "@/context/AuthContext";
import Loader from "../ui/Loader";
import ForgotPasswordModal from "./ForgotPasswordModal";

type SignInFormValues = {
  email: string;
  password: string;
  staySignedIn: boolean;
};

export default function SignInForm() {
  const { register, handleSubmit, formState: { errors }, watch } = useForm<SignInFormValues>();
  const { login, isLoading } = useAuth();
  const router = useRouter();
  const [invalid, setInvalid] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [forgotModalOpen, setForgotModalOpen] = useState(false);
  const watchedEmail = watch("email");

  const onSubmit = async (data: SignInFormValues) => {
    try {
      setInvalid(false);
      await login({
        email: data.email,
        password: data.password,
        staySignedIn: data.staySignedIn,
      });
      
      setAttempts(0);
      router.push("/choose-business-unit");
    } catch (error) {
      console.error("Login error:", error);
      setInvalid(true);
      setAttempts((prev) => {
        const newCount = prev + 1;
        if (newCount >= 2) {
          setModalOpen(true);
        }
        return newCount;
      });
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className={`space-y-${invalid ? '4' : '6'}`}>
        <Input
          type="text"
          placeholder="Enter your login id"
          {...register("email", { required: "Email Id is required" })}
          error={errors.email?.message}
        />

        <Input
          id="password"
          type="password"
          placeholder="Password"
          {...register("password", { required: "Password is required" })}
          error={errors.password?.message}
          showPasswordToggle={true}
        />

        {invalid && (
          <div className="p-3 bg-red-100 rounded-lg text-red-600 text-center text-sm">
            Login failed. Username or password is incorrect
          </div>
        )}

        <div className="flex items-center justify-between text-base text-gray-600">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                {...register("staySignedIn")}
                className="appearance-none w-5 h-5 border border-gray-400 rounded-full 
                           checked:bg-brand-blue checked:border-brand-blue
                           checked:bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20viewBox%3D%220%200%2016%2016%22%20fill%3D%22white%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org/2000/svg%22%3E%3Cpath%20d%3D%22M12.207%204.793a1%201%200%20010%201.414l-5%205a1%201%200%2001-1.414%200l-2-2a1%201%200%20011.414-1.414L6.5%209.086l4.293-4.293a1%201%200%20011.414%200z%22%2F%3E%3C%2Fsvg%3E')]
                           checked:bg-center checked:bg-no-repeat"
              />
              Stay signed in
            </label>
            <button
              type="button"
              className="text-brand-blue hover:underline"
              onClick={() => setForgotModalOpen(true)}
            >
              Forgot password?
            </button>
        </div>


        <Button
          type="submit"
          variant="secondary"
          size="md"
          fullWidth={true}
          disabled={isLoading}
        >
          {isLoading ? <Loader /> : "Sign In"}
        </Button>
      </form>

      <WarningModal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
        <p className="text-3xl text-brand-blue">
          Please contact
          <br />
          your sales representative
        </p>
      </WarningModal>

      <ForgotPasswordModal
        isOpen={forgotModalOpen}
        onClose={() => setForgotModalOpen(false)}
        email={watchedEmail}
      />
    </>
  );
}